import { useMemo, useState, useRef, useEffect } from 'react'
import { AutoComplete, Button, Flex, Input } from 'antd'
import $t from '../../../i18n'
import AnimatedLoadingLabel from '../AnimatedLoadingLabel'
import { useIsMobile } from 'hooks'
import './AutocompleteInput.css'

const Title = ({ title }) => (
  <Flex
    align='center'
    justify='space-between'
    className='autocomplete-group-title'
  >
    <span>{title}</span>
  </Flex>
)

const AutocompleteInput = ({
  value,
  placeholder,
  className,
  errorText,
  isLoading = false,
  options: optionsProp,
  optionGroups = [],
  renderOption = () => {},
  onInputChange,
  onSelect,
  onSearch,
  useSearchInput = false,
  onSearchButtonClick,
  isSearchDisabled = false,
  noResultsLocation,
  viewMode = 'dropdown', // 'dropdown' or 'list'
  renderOptionsList = () => {},
  shouldShowEmptyStateCondition = true,
  isReadOnly = false,
  ...props
}) => {
  const inputRef = useRef(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [initialValue] = useState(value)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus({
        cursor: 'end'
      })
    }
  }, [inputRef])

  const options = useMemo(() => {
    if (!renderOption && !renderOptionsList) {
      return []
    }

    if (optionsProp) {
      return optionsProp
    }

    return optionGroups.map(group => ({
      label: <Title title={group.label} />,
      options: group.options.map((option, index) => ({
        key: option.id || `${group.label}-${index}`,
        value: option.name,
        label: renderOption(option),
        data: option
      }))
    }))
  }, [optionsProp, optionGroups, renderOption, renderOptionsList])

  function handleSelect(value, option) {
    setIsDropdownOpen(false)
    const optionData = option?.data || value
    if (onSelect && optionData) {
      onSelect(optionData, optionData.type)
    }
  }

  function handleChange(value) {
    const newValue = value || ''
    if (viewMode === 'dropdown' && !isMobile) {
      setIsDropdownOpen(newValue.length >= 2)
    }
    if (onInputChange) {
      onInputChange({ target: { value: newValue } })
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && onSearch) {
      setIsDropdownOpen(false)
      onSearch(value)
    }
  }

  function handleBlur() {
    setIsDropdownOpen(false)
  }

  function handleSearchButtonClick() {
    setIsDropdownOpen(false)
    inputRef.current?.blur()
    if (onSearchButtonClick) {
      onSearchButtonClick()
    }
  }

  function getNotFoundContent() {
    if (value?.length < 2) {
      return null
    }

    if (
      !isLoading &&
      options.length === 0 &&
      value !== initialValue &&
      shouldShowEmptyStateCondition
    ) {
      if (noResultsLocation) {
        return $t('search.noResultsInLocation', {
          query: value,
          location: noResultsLocation
        })
      } else {
        return $t('search.noResults', { query: value })
      }
    }
  }

  function renderInput() {
    if (viewMode === 'list') {
      return (
        <div className='mobile-search-input-wrapper'>
          <Input
            type='text'
            value={value}
            onChange={onInputChange}
            placeholder={placeholder}
            className='mobile-search-input'
            ref={inputRef}
            allowClear={
              !isReadOnly && {
                clearIcon: <i className='icon-oclose input-clear-icon' />
              }
            }
            disabled={isReadOnly}
          />
          {useSearchInput ? (
            <Button
              type='primary'
              onClick={onSearchButtonClick}
              disabled={isSearchDisabled || isReadOnly}
              className='mobile-search-button'
            >
              <i className='icon-search' />
            </Button>
          ) : null}
        </div>
      )
    }

    return (
      <>
        {useSearchInput ? (
          <AutoComplete
            value={value || ''}
            options={options}
            onSelect={handleSelect}
            onChange={handleChange}
            onBlur={handleBlur}
            className={'search-autocomplete' + (className || '')}
            loading={isLoading}
            allowClear={
              !isReadOnly && {
                clearIcon: <i className='icon-oclose input-clear-icon' />
              }
            }
            readOnly={isMobile}
            autoFocus={true}
            notFoundContent={getNotFoundContent()}
            open={isDropdownOpen}
            disabled={isReadOnly}
            {...props}
          >
            <Input.Search
              placeholder={placeholder}
              variant='outlined'
              onSearch={
                isSearchDisabled || isReadOnly
                  ? () => {}
                  : handleSearchButtonClick
              }
              enterButton
              className={
                isSearchDisabled || isReadOnly ? 'search-disabled' : ''
              }
              ref={inputRef}
              disabled={isReadOnly}
            />
          </AutoComplete>
        ) : (
          <AutoComplete
            value={value || ''}
            options={options}
            onSelect={handleSelect}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={placeholder}
            variant='filled'
            className={'search-autocomplete' + (className || '')}
            loading={isLoading}
            allowClear={
              !isReadOnly && {
                clearIcon: <i className='icon-oclose input-clear-icon' />
              }
            }
            readOnly={isMobile}
            autoFocus={true}
            notFoundContent={getNotFoundContent()}
            prefix={<i className='icon-search' />}
            open={isDropdownOpen}
            disabled={isReadOnly}
            {...props}
          >
            <Input ref={inputRef} />
          </AutoComplete>
        )}
      </>
    )
  }

  return (
    <div className='autocomplete-input-container'>
      <div
        className={
          errorText
            ? 'float-label material-input-container error'
            : 'float-label material-input-container'
        }
      >
        {renderInput()}
        {viewMode === 'list' && (
          <>
            <div className='mobile-loading-container'>
              {isLoading && <AnimatedLoadingLabel />}
            </div>
            <div className='mobile-options-container'>
              {renderOptionsList()}
            </div>
            <div className='no-results-message'>{getNotFoundContent()}</div>
          </>
        )}
      </div>
    </div>
  )
}

export default AutocompleteInput
